import { clsx } from "clsx";
import { ChangeEvent, useState } from "react";
import { ReportStepOne } from "./ReportStepOne";
import { ReportStepTwo } from "./ReportStepTwo";
import { Popup } from "@/components/popups/Popup";
import "./styles/ReportPopup.scss";
import { IReportPopupProps } from "@/common/buses/popups/popup-types";
import { onCloseReportPopup } from "@/components/popup-dispatcher/PopupDispatcher";
import { apiMain } from "@/common/services";
import { AxiosError } from "axios";
import { IErrorData } from "@/common/services/types";
import { showNotification } from "@/components/notifications/Notifications";

export interface IReason {
  value: number;
  text: string;
}

const REPORT_REASONS: IReason[] = [
  { value: 0, text: "Underaged" },
  { value: 1, text: "Racist / Hate-Based Language Or Actions" },
  { value: 2, text: "Animals / Acts Of Bestiality" },
  { value: 3, text: "Rape / Sexual Assault" },
  { value: 4, text: "Violence / Death / Disturbing Content" },
  { value: 5, text: "Copyright / I Own This Content" },
  { value: 6, text: "I Am In This Content" },
  { value: 7, text: "Other" },
];

export const ReportPopup = ({ gifId }: IReportPopupProps) => {
  const [reportReason, setReportReason] = useState<IReason>(REPORT_REASONS[0]);
  const [reportMessage, setReportMessage] = useState<string>("");
  const [isCanContact, setIsCanContact] = useState<boolean>(false);

  const handleReportMessage = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setReportMessage(event.target.value);
  };

  const handleIsCanContact = () => {
    setIsCanContact(!isCanContact);
  };

  const [currentStep, setCurrentStep] = useState<number>(0);

  const nextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const handleToggle = () => {
    setCurrentStep(0);
    setReportMessage("");
    setReportReason(REPORT_REASONS[0]);
    setIsCanContact(false);
    onCloseReportPopup();
  };

  const report = async () => {
    try {
      await apiMain.reportGif({
        gifId: gifId,
        option: reportReason.value,
        message: reportMessage,
        canContact: isCanContact,
        email: ""
      });
      showNotification({
        title: "Report has been sent",
        variant: "success",
      });
    } catch (e) {
      const err = e as AxiosError<IErrorData>;
      const message = err.response?.data?.error?.message ||
        "An error occurred, please try again later";

      showNotification({
        title: message,
        variant: "error",
      });
    }
    handleToggle();
  };

  const btnProps = {
    variant: 1,
    title: currentStep === 0 ? "Next" : "Submit",
    onClick: currentStep === 0 ? nextStep : report
  };

  const popupClx = clsx(
    "ReportPopup",
    { "ReportPopup_StepOne": currentStep === 0 },
    { "ReportPopup_StepTwo": currentStep === 1 },
  );

  return (
    <Popup
      title="Report Content"
      isOpen={true}
      popupClass={popupClx}
      btnProps={btnProps}
      onClose={handleToggle}
    >
      {currentStep === 0 && (
        <ReportStepOne
          reportReasons={REPORT_REASONS}
          reportReason={reportReason}
          setReportReason={setReportReason}
          nextStep={nextStep}
        />
      )}
      {currentStep === 1 && (
        <ReportStepTwo
          comment={reportMessage}
          onChangeComment={handleReportMessage}
          isCanContact={isCanContact}
          handleIsCanContact={handleIsCanContact}
          report={report}
        />
      )}
    </Popup>
  );
};
